// C1: 多 Room 管理 —— 真实 storage 驱动，listRooms 异步 + bus 订阅刷新
//
// 交互：
//   "+" → prompt 输入 room 名称 → upsertRoom + navigate
//   右键 RoomCard → 浮层菜单：重命名 / 清空历史 / 删除
//   非活跃 Room 收消息 → unread++（由 use-room.ts 触发 bumpRoomActivity）
//   切到某 Room → setReadCursor 清未读（由 use-room.ts 触发）

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useI18n } from "../lib/i18n";
import { notifyRoomsChanged, onRoomsChanged } from "../lib/room-bus";
import { getStorage } from "../lib/storage";
import type { StoredRoom } from "../lib/storage";

import { useDialog } from "./Dialog";
import { RoomCard, type RoomSummary } from "./RoomCard";
import { RoomContextMenu } from "./RoomContextMenu";

function toSummary(r: StoredRoom): RoomSummary {
  return {
    id: r.id,
    name: r.name,
    lastMessage: r.lastMsgText ?? "（暂无消息）",
    lastTs: r.lastMsgTs ? formatTs(r.lastMsgTs) : "—",
    unread: r.unread,
    hasMention: r.hasMention,
  };
}

function formatTs(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  }
  const dayMs = 86_400_000;
  const diffDays = Math.floor((now.getTime() - ts) / dayMs);
  if (diffDays < 7) return `${diffDays}天前`;
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function slugify(name: string): string {
  return (
    name
      .trim()
      .toLowerCase()
      .replace(/[^\w一-龥]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 32) || `room-${Date.now().toString(36)}`
  );
}

export function RoomList() {
  const [rooms, setRooms] = useState<StoredRoom[] | null>(null);
  const [menu, setMenu] = useState<{
    room: StoredRoom;
    x: number;
    y: number;
  } | null>(null);
  const navigate = useNavigate();
  const dlg = useDialog();
  const { t } = useI18n();

  const refresh = useCallback(async () => {
    const s = await getStorage();
    const list = await s.listRooms();
    list.sort((a, b) => (b.lastMsgTs ?? 0) - (a.lastMsgTs ?? 0));
    setRooms(list);
  }, []);

  // 启动 + 监听 bus
  useEffect(() => {
    refresh();
    return onRoomsChanged(refresh);
  }, [refresh]);

  // 空列表自动建 default Room
  useEffect(() => {
    if (rooms && rooms.length === 0) {
      (async () => {
        const s = await getStorage();
        await s.upsertRoom({
          id: "default",
          name: "主会话",
          lastMsgId: null,
          lastMsgText: null,
          lastMsgTs: null,
          unread: 0,
          hasMention: false,
          readCursor: 0,
          joinedAt: Date.now(),
          pinned: false,
        });
        notifyRoomsChanged();
      })();
    }
  }, [rooms]);

  const handleCreate = useCallback(async () => {
    const name = await dlg.prompt({
      title: t("room.create.title"),
      label: t("room.create.label"),
      placeholder: t("room.create.placeholder"),
      confirmLabel: t("room.create.confirm"),
    });
    if (!name || !name.trim()) return;
    const s = await getStorage();
    const trimmed = name.trim();
    // 同名也可，但 id 唯一：碰撞时加后缀
    let id = slugify(trimmed);
    if (await s.getRoom(id)) {
      id = `${id}-${Date.now().toString(36).slice(-4)}`;
    }
    await s.upsertRoom({
      id,
      name: trimmed,
      lastMsgId: null,
      lastMsgText: null,
      lastMsgTs: null,
      unread: 0,
      hasMention: false,
      readCursor: 0,
      joinedAt: Date.now(),
      pinned: false,
    });
    notifyRoomsChanged();
    navigate(`/rooms/${id}`);
  }, [dlg, navigate]);

  const handleContext = useCallback(
    (room: StoredRoom, e: React.MouseEvent) => {
      e.preventDefault();
      setMenu({ room, x: e.clientX, y: e.clientY });
    },
    [],
  );

  const handleRename = useCallback(
    async (room: StoredRoom) => {
      setMenu(null);
      const newName = await dlg.prompt({
        title: t("room.menu.rename.title"),
        label: t("room.menu.rename.label"),
        defaultValue: room.name,
        confirmLabel: t("room.menu.rename.confirm"),
      });
      if (!newName || !newName.trim() || newName.trim() === room.name) return;
      const s = await getStorage();
      await s.upsertRoom({ ...room, name: newName.trim() });
      notifyRoomsChanged();
    },
    [dlg, t],
  );

  const handleClearHistory = useCallback(
    async (room: StoredRoom) => {
      setMenu(null);
      const ok = await dlg.confirm({
        title: t("room.menu.clear"),
        message: t("room.menu.clear.confirm", { name: room.name }),
        confirmLabel: t("room.menu.clear.btn"),
        variant: "danger",
      });
      if (!ok) return;
      const s = await getStorage();
      await s.deleteEventsOfRoom(room.id);
      await s.upsertRoom({
        ...room,
        lastMsgId: null,
        lastMsgText: null,
        lastMsgTs: null,
        unread: 0,
        hasMention: false,
        readCursor: 0,
      });
      notifyRoomsChanged();
    },
    [dlg],
  );

  const handleDelete = useCallback(
    async (room: StoredRoom) => {
      setMenu(null);
      const ok = await dlg.confirm({
        title: t("room.menu.delete"),
        message: t("room.menu.delete.confirm", { name: room.name }),
        confirmLabel: t("room.menu.delete.btn"),
        variant: "danger",
      });
      if (!ok) return;
      const s = await getStorage();
      await s.deleteEventsOfRoom(room.id);
      await s.deleteRoom(room.id);
      notifyRoomsChanged();
      // 如果当前活跃 Room 就是它，跳回 /rooms（EmptyState）
      if (
        typeof window !== "undefined" &&
        window.location.hash.includes(`/rooms/${room.id}`)
      ) {
        navigate("/rooms");
      }
    },
    [dlg, navigate],
  );

  const summaries = useMemo(
    () => (rooms ?? []).map(toSummary),
    [rooms],
  );

  return (
    <aside className="room-list" aria-label={t("room.list.title")}>
      <header className="room-list__head">
        <span className="room-list__title">{t("room.list.title")}</span>
        <button
          type="button"
          className="room-list__new"
          onClick={handleCreate}
          title={t("room.list.new")}
        >
          +
        </button>
      </header>
      <nav className="room-list__items">
        {summaries.length === 0 && rooms !== null ? (
          <div className="room-list__empty">
            <p className="muted small">{t("room.list.empty")}</p>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleCreate}
            >
              {t("room.list.empty.cta")}
            </button>
          </div>
        ) : (
          summaries.map((r, i) => (
            <div
              key={r.id}
              onContextMenu={(e) => rooms && handleContext(rooms[i], e)}
            >
              <RoomCard room={r} />
            </div>
          ))
        )}
      </nav>
      {menu && (
        <RoomContextMenu
          x={menu.x}
          y={menu.y}
          room={menu.room}
          onRename={handleRename}
          onClearHistory={handleClearHistory}
          onDelete={handleDelete}
          onDismiss={() => setMenu(null)}
        />
      )}
    </aside>
  );
}
